// ---------------------------------------------------------------------------
// C101-C103: Vertical Templates — config, seed, browse, apply
// ---------------------------------------------------------------------------
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

export interface VerticalTemplate {
  id: string;
  name: string;
  industry: string;
  category: string;
  description: string;
  iconUrl: string | null;
  pipelineStageCount: number;
  serviceCount: number;
  faqCount: number;
  isApplied: boolean;
}

export interface VerticalTemplateDetail extends VerticalTemplate {
  pipelineStages: Array<{ name: string; order: number }>;
  services: Array<{ name: string; duration: number; price: number }>;
  faqs: Array<{ question: string; answer: string }>;
  customFields: Array<{ name: string; type: string }>;
  aiConfig: Record<string, unknown>;
  benchmarks: Record<string, unknown>;
}

export const TEMPLATE_CATEGORIES = [
  "Health & Wellness", "Professional Services", "Trades & Home Services",
  "Creative", "Food & Beverage", "Education", "Beauty & Personal Care",
  "Real Estate", "Fitness", "Automotive",
] as const;

interface PickOneCustomizationPathInput {
  templateVertical: string;
  starterPageId?: string;
  siteId?: string;
}

export function buildPickOneCustomizationPath({
  templateVertical,
  starterPageId,
  siteId,
}: PickOneCustomizationPathInput): string {
  const searchParams = new URLSearchParams({
    entry: "pick-one",
    template: templateVertical,
  });

  if (starterPageId) {
    searchParams.set("starter", starterPageId);
  }

  if (siteId) {
    searchParams.set("siteId", siteId);
  }

  return `/pages/settings?${searchParams.toString()}`;
}

export function useVerticalTemplates(category?: string) {
  const [templates, setTemplates] = useState<VerticalTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const url = category ? `/api/platform/templates?category=${encodeURIComponent(category)}` : "/api/platform/templates";
    apiFetch<{ data: VerticalTemplate[] }>(url)
      .then((res) => setTemplates(res.data ?? []))
      .catch(() => setTemplates([]))
      .finally(() => setIsLoading(false));
  }, [category]);

  return { templates, isLoading };
}

export function useVerticalTemplateDetail(templateId: string | undefined) {
  const [template, setTemplate] = useState<VerticalTemplateDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!templateId) { setTemplate(null); return; }
    setIsLoading(true);
    apiFetch<{ data: VerticalTemplateDetail }>(`/api/platform/templates/${templateId}`)
      .then((res) => setTemplate(res.data))
      .catch(() => setTemplate(null))
      .finally(() => setIsLoading(false));
  }, [templateId]);

  return { template, isLoading };
}

export function useApplyTemplate() {
  const [isApplying, setIsApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback(async (templateId: string) => {
    setIsApplying(true);
    setProgress(0);
    setError(null);
    try {
      setProgress(20);
      await apiFetch(`/api/platform/templates/${templateId}/apply`, { method: "POST" });
      setProgress(100);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply template");
    } finally {
      setIsApplying(false);
    }
  }, []);

  return { apply, isApplying, progress, error };
}
